import { Equipment, EquipmentCategory } from '@dh-io-emh/commerce-api/src/models/shared/ocb';
import { infuse } from '../../ioc-config';

export interface Lines {
  interior?: string;
  exterior?: string;
}

export type EquipmentCategories = {
  highlights: EquipmentCategory[];
  details: EquipmentCategory[];
};

export type ExtendedEquipment = Equipment & { sortId: string; mainCategoryId: string };

// Adds additional properties to the Enrichment DTO required by FE components
const extendEquipments = (
  equipments: Equipment[] = [],
  sortId: string,
  mainCategoryId: string,
): ExtendedEquipment[] => {
  return equipments.map((equipment) => ({
    ...equipment,
    sortId,
    mainCategoryId,
  }));
};

// Given a category iterate through all subcategories and return a flattened list of all equipments
// We don't need any subcategories or sub levels of equipments on the PDP
// so we have to place all of them to the first level of the category object
// to loop through it in the template
const flattenEquipments = (
  category: EquipmentCategory,
  mainCategoryId: string,
  sortId: string,
  equipments: ExtendedEquipment[] = [],
): ExtendedEquipment[] => {
  // Combine sortIds
  const combinedSortId = sortId + category.sortId + '';

  // Create a new SortableEquipment object
  const categoryEquipments: ExtendedEquipment[] = extendEquipments(category.equipments, combinedSortId, mainCategoryId);

  equipments.push(...categoryEquipments);

  // If there are any subcategories provided, get all sub equipments recursively
  if (category.subcategories) {
    category.subcategories.forEach((subcategory) => {
      flattenEquipments(subcategory, mainCategoryId, sortId, equipments);
    });
  }

  return equipments;
};

export const adjustEnrichmentEquipments = (categories: EquipmentCategory[] = []): EquipmentCategories => {
  const {
    pdp: { equipments },
  } = infuse('config');

  // Create equipments categories from a mapping table
  categories.forEach((category) => {
    // Combine all equipments in one array
    category.equipments = flattenEquipments(category, category.id, category.sortId!.toString());

    (category.equipments as ExtendedEquipment[]).sort((a, b) => a.sortId!.localeCompare(b.sortId!));
  });

  // Either use legacy way of extracting categories from the EXTRAS category
  // or simply filter them by the excludedCategories defined in the config
  const detailsCategories = categories.filter((item) => {
    return !(equipments?.excludedCategories || []).includes(item.id);
  });

  return {
    highlights: categories,
    details: detailsCategories,
  };
};

// Flatten all categories and subcategories into one array
const flattenCategories = (categories: EquipmentCategory[] = []): EquipmentCategory[] => {
  const combinedCategories = [...categories];

  categories.forEach((category) => {
    // Check if there're any subcategories and push them recursively to the array
    if (category.subcategories?.length) {
      combinedCategories.push(...flattenCategories(category.subcategories));
    }
  });

  return combinedCategories;
};

// The data from the Enrichment and Product APIs aren't perfect
// so we have to map it to make it work for us
export const getLines = (categories: EquipmentCategory[] = []): Lines | undefined => {
  if (categories.length === 0) {
    return undefined;
  }

  const mappedLineIds = {
    interior: ['ZK096', 'ZK018'],
    exterior: ['ZK096', 'ZK017'],
  };

  // Helper function to find and map a list of line ids to the category objects
  const findLines = (lineIds: string[], categories: EquipmentCategory[]): string | undefined => {
    return (
      lineIds
        // Map lineId to the category object: 'ZK018' -> EquipmentCategory
        .map((id) => categories.find((category) => id === category.id))
        // filter undefined values if there are no lines
        // get a name of the line
        .filter((category) => !!category)[0]?.equipments?.[0].name
    );
  };

  // Combine all categories and subcategories into one array
  const combinedCategories = flattenCategories(categories);

  return {
    interior: findLines(mappedLineIds.interior, combinedCategories),
    exterior: findLines(mappedLineIds.exterior, combinedCategories),
  };
};
